import React, { useEffect, useState } from 'react';
import InfoTip from '../components/InfoTip';
import { ChevronLeft, ChevronRight, ClipboardCheck, RefreshCw } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { navLabel } from '../utils/navigation';
import MonthPicker from '../components/MonthPicker';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import StandingChip from '../components/compliance/StandingChip';
import { STANDARDS, StandardId } from '../components/compliance/types';
import HistoryStrip from '../components/audit/HistoryStrip';
import { FlowList, NamingList, PlanningList, TimelogList } from '../components/audit/AuditLists';
import { AuditSummary, FlowItem, HistoryPoint, NamingItem, PlanningData, TimelogData } from '../components/audit/types';
import * as fmt from '../lib/format';
import { shiftMonth } from '../utils/urlParams';
import { usePeriodParams } from '../hooks/usePeriodParams';
import { useTabParam } from '../hooks/useTabParam';

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';
const ICON_BUTTON =
  'rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-white hover:text-gray-900 active:scale-[0.96] disabled:opacity-30 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';

const PANELS: Record<StandardId, { title: string; intro: (month: string) => string; empty: string }> = {
  naming: {
    title: 'Ticket titles',
    intro: (m) => `Tickets you created or own, created in ${m}, whose titles could be clearer. Rename them in Jira and they drop off on the next sync.`,
    empty: 'Every title of yours is clear.',
  },
  flow: {
    title: 'Moves outside the usual owner',
    intro: (m) => `Moves you made in ${m} into a status that another function usually moves. Hand it over with a comment.`,
    empty: 'None of your moves went outside the usual owner.',
  },
  timelog: {
    title: 'Time logging',
    intro: (m) => `Worklog entries from ${m} that do not start with one standard tag, and tickets of yours in progress with no time logged.`,
    empty: 'Your entries all have one standard tag, and nothing of yours is in progress without time.',
  },
  planning: {
    title: 'Tickets carried over often',
    intro: () => 'Your open tickets that have been carried over to a later sprint more times than the review limit. A lead can help split or drop them.',
    empty: 'None of your open tickets is above the carry-over limit.',
  },
};

type ListData = NamingItem[] | FlowItem[] | TimelogData | PlanningData;

// My audit: your own work against the four standards. Only your own items are ever shown; the page
// takes no account from the address. Read only: fix an item in Jira and it drops off.
export default function MyAuditPage() {
  const { user } = useAuth();
  // The month and the standard on show live in the address, so a reload or a copied link opens the same view.
  const { period, current, isCurrent, setPeriod } = usePeriodParams();
  const [selected, setSelected] = useTabParam<StandardId>(['naming', 'flow', 'timelog', 'planning'], 'naming', 'standard');
  const [refreshKey, setRefreshKey] = useState(0);

  const [summary, setSummary] = useState<AuditSummary | null>(null);
  const [history, setHistory] = useState<HistoryPoint[]>([]);
  const [unlinked, setUnlinked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lists, setLists] = useState<Record<string, ListData>>({});
  const [loadingList, setLoadingList] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The numbers for the month, and the six months before it.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([apiService.getMyAudit('summary', period), apiService.getMyAudit('history', period)])
      .then(([s, h]) => {
        if (cancelled) return;
        if (!s.success) return setError(s.error || 'Could not load your audit');
        setError(null);
        setUnlinked(!!s.meta?.unlinked);
        setSummary(s.data);
        setHistory(h.success && h.data ? h.data.months : []);
      })
      .catch((e: any) => !cancelled && setError(e?.response?.data?.error || 'Could not load your audit'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [period, refreshKey]);

  // The list behind the chosen standard, kept while you switch back and forth.
  const key = `${selected}:${period}`;
  useEffect(() => {
    if (unlinked || !summary || lists[key]) return;
    let cancelled = false;
    setLoadingList(true);
    apiService
      .getMyAudit(selected, period)
      .then((r) => {
        if (cancelled) return;
        if (r.success && r.data) {
          const d = r.data;
          setLists((prev) => ({ ...prev, [key]: selected === 'naming' || selected === 'flow' ? d.items : d }));
        } else setError(r.error || 'Could not load the list');
      })
      .catch((e: any) => !cancelled && setError(e?.response?.data?.error || 'Could not load the list'))
      .finally(() => !cancelled && setLoadingList(false));
    return () => {
      cancelled = true;
    };
  }, [key, selected, period, summary, unlinked, lists]);

  const refresh = () => {
    setLists({});
    setRefreshKey((n) => n + 1);
  };
  const changePeriod = (p: string) => {
    setPeriod(p);
    setError(null);
  };

  const month = fmt.month(period);
  const panel = PANELS[selected];
  const data = lists[key];
  const count = summary?.to_fix[selected] ?? 0;
  const label = STANDARDS.find((s) => s.id === selected)!.label;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100/60">
      <PageHeader title={navLabel('/my-audit')} subtitle="Your own work against the four standards" icon={ClipboardCheck} />
      <div className="p-4 sm:p-6">
        <div className="mx-auto max-w-7xl">
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1">
              <button type="button" aria-label="Previous month" onClick={() => changePeriod(shiftMonth(period, -1))} className={ICON_BUTTON}>
                <ChevronLeft className="h-4 w-4" />
              </button>
              <MonthPicker value={period} max={current} onChange={changePeriod} />
              <button type="button" aria-label="Next month" disabled={isCurrent} onClick={() => changePeriod(shiftMonth(period, 1))} className={ICON_BUTTON}>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            {summary && (
              <p className="text-sm text-gray-500">
                Compared with {fmt.month(summary.previous_period)}
                {isCurrent && ' · this month is still in progress'}
                <InfoTip id="compliance.change" />
              </p>
            )}
            <div className="ml-auto">
              <button type="button" onClick={refresh} aria-label="Refresh" className={ICON_BUTTON}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin motion-reduce:animate-none' : ''}`} />
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-4 rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700 ring-1 ring-rose-200" role="alert">
              {error}
            </div>
          )}

          {loading && !summary ? (
            <div className="space-y-4" role="status" aria-label="Loading your audit">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className={`${CARD} h-28 animate-pulse motion-reduce:animate-none`} />
                ))}
              </div>
              <div className={`${CARD} h-48 animate-pulse motion-reduce:animate-none`} />
            </div>
          ) : unlinked ? (
            <div className={`${CARD} px-6 py-12 text-center`}>
              <p className="text-sm text-gray-700">Your login is not linked to a Jira account yet, so there is nothing of yours to show.</p>
              <p className="mt-1 text-sm text-gray-500">{user?.role === 'admin' ? 'Link it in Settings → Create users.' : 'Ask an admin to link it in Settings → Create users.'}</p>
            </div>
          ) : summary ? (
            <div className={loading ? 'opacity-70 transition-opacity motion-reduce:transition-none' : ''}>
              <div role="group" aria-label={`Your standards in ${month}`} className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
                {STANDARDS.map((s) => (
                  <StandingChip
                    key={s.id}
                    standard={s.id}
                    standing={summary.standards[s.id]}
                    onPress={() => setSelected(s.id)}
                    pressLabel={`Show your ${s.label} items`}
                    selected={selected === s.id}
                    large
                  />
                ))}
              </div>

              <section className={`${CARD} p-5`} aria-labelledby="audit-panel-title">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_auto]">
                  <div className="min-w-0">
                    <div className="flex items-baseline gap-2">
                      <h2 id="audit-panel-title" className="text-base font-semibold text-gray-900">{panel.title}</h2>
                      {selected === 'planning' && <InfoTip id="planning.rollover" className="" />}
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold tabular-nums text-gray-700">{fmt.count(count)}</span>
                    </div>
                    <p className="mt-1 text-sm text-gray-600">{panel.intro(month)}</p>

                    <div className="mt-4" aria-live="polite">
                      {loadingList && !data ? (
                        <div className="space-y-2.5" role="status" aria-label="Loading the list">
                          {[0, 1].map((i) => (
                            <div key={i} className="h-20 animate-pulse rounded-xl bg-gray-100 motion-reduce:animate-none" />
                          ))}
                        </div>
                      ) : data && count > 0 ? (
                        selected === 'naming' ? (
                          <NamingList items={data as NamingItem[]} />
                        ) : selected === 'flow' ? (
                          <FlowList items={data as FlowItem[]} />
                        ) : selected === 'timelog' ? (
                          <TimelogList data={data as TimelogData} />
                        ) : (
                          <PlanningList data={data as PlanningData} />
                        )
                      ) : data ? (
                        <div className="rounded-xl bg-emerald-50/60 px-4 py-6 text-center text-sm text-emerald-900 ring-1 ring-inset ring-emerald-100">
                          {selected === 'timelog' && !(data as TimelogData).tagging_started
                            ? `The tagging standard started later than ${month}, so there are no entries to judge.`
                            : summary.standards[selected]?.pct === null && selected !== 'planning'
                              ? selected === 'flow'
                                ? 'None of your moves could be judged this month. Flow roles are assigned by an admin.'
                                : 'Nothing to judge this month.'
                              : panel.empty}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="lg:pl-2">
                    <HistoryStrip points={history} standard={selected} label={label} current={period} />
                  </div>
                </div>
              </section>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
